import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserProfile, fetchUserProfileByToken } from './store/userSlice';
import Header from './components/Header';
import Hero from './components/Hero';
import AlertModal from './components/AlertModal';
import FloatingMenu from './components/FloatingMenu';
import FeatureSection from './components/FeatureSection';
import Footer from './components/Footer';
import LoginModal from './components/LoginModal';
import AfterSales from './components/AfterSales';
import PageHeader from './components/PageHeader';
import HelpSection from './components/HelpSection';
import Purchases from './components/Purchases';
import ProductDetail from './components/ProductDetail';
import ProductDetailsVertical from './components/ProductDetailsVertical';
import RelatedProducts from './components/RelatedProducts';
import OrderForm from './components/OrderForm';
import OrderSummary from './components/OrderSummary';
import OrderConfirmation from './components/OrderConfirmation';
import ProductSeriesPage from './components/ProductSeriesPage';
import ServerFleet from './components/ServerFleet';
import HomeProductSection from './components/home/HomeProductSection';
import HomeToolsSection from './components/home/HomeToolsSection';
import InvitePage from './components/InvitePage';
import Intercom from '@intercom/messenger-js-sdk';
import SiteTransferNotice from './components/SiteTransferNotice';
import AutoLogin from './components/AutoLogin';
import { getWebInfo } from './api/user'; // 导入你的API
import '@fortawesome/fontawesome-free/css/all.min.css';
import { useTranslation } from 'react-i18next';


export const useAnnouncement = (defaultTitle) => {
  const [announcement, setAnnouncement] = useState(defaultTitle || "加载中...");
  
  useEffect(() => {
    const fetchAnnouncement = async () => {
      try {
        const response = await getWebInfo({});
        if (response && response.code === "0" && response.info) {
          setAnnouncement(response.info);
        }
      } catch (error) {
        console.error('获取公告失败:', error);
        if (defaultTitle) {
          setAnnouncement(defaultTitle);
        }
      }
    };
    
    fetchAnnouncement();
  }, [defaultTitle]);
  
  return announcement;
};


// 镜像账户产品数据
const mirrorProducts = [
  { name: '网站A镜像账户', price: '9.99', icon: 'fas fa-microchip', id: 'website-a-mirror' },
  { name: '网站B镜像账户', price: '14.99', icon: 'fas fa-brain', id: 'website-b-mirror' },
  { name: '网站C镜像账户', price: '12.49', icon: 'fas fa-robot', id: 'website-c-mirror' },
  { name: '网站D镜像账户', price: '19.99', icon: 'fas fa-laptop-code', id: 'website-d-mirror' },
];

// 成品号产品数据
const finishedProducts = [
  { name: '网站E成品号', price: '24.99', icon: 'fas fa-database', id: 'website-e-finished' },
  { name: '网站F成品号', price: '29.99', icon: 'fas fa-server', id: 'website-f-finished' },
  { name: '网站G成品号', price: '19.99', icon: 'fas fa-network-wired', id: 'website-g-finished' },
  { name: '网站H成品号', price: '15.99', icon: 'fas fa-code', id: 'website-h-finished' },
];

// 功能亮点数据
const features = [
  { title: '私密安全', desc: '所有对话和创作内容100%保密，仅您自己可见，数据安全无忧，告别共享烦恼。', icon: 'fas fa-shield-alt' },
  { title: '无限使用', desc: '完全不限总次数，无封号风险，随时随地满足您的所有AI创作和咨询需求。', icon: 'fas fa-bolt' },
  { title: '超低价格', desc: '每天仅需1元，比一杯奶茶还便宜，让顶级AI工具成为人人可负担的日常服务。', icon: 'fas fa-dollar-sign' },
  { title: '工具齐全', desc: '一站集成GPT、Grok、Midjourney等全球顶尖AI工具，无需多处注册和切换。', icon: 'fas fa-globe' },
];



const App = () => {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  // 添加token登录的加载状态
  const [isTokenLoginLoading, setIsTokenLoginLoading] = useState(false);
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const location = useLocation();
  const navigate = useNavigate(); // 添加 useNavigate
  const { t } = useTranslation();


  // 监听路由变化，滚动到顶部
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [location.pathname]);




  

  // 检查用户登录状态并获取用户资料 - 合并两个useEffect为一个
  useEffect(() => {
    // 从hash或search中提取token
    let tokenToUse = null;
    
    // 检查location.hash中是否包含token（针对 /list/#/?url_login_token=... 格式）
    if (location.hash && location.hash.includes('url_login_token=')) {
      const hashIndex = location.hash.indexOf('?');
      if (hashIndex !== -1) {
        const hashParams = new URLSearchParams(location.hash.substring(hashIndex));
        tokenToUse = hashParams.get('url_login_token');
      }
    }
    
    // 如果hash中没找到，则检查常规查询参数
    if (!tokenToUse) {
      const urlParams = new URLSearchParams(location.search);
      tokenToUse = urlParams.get('url_login_token');
    }
    
    console.log('当前URL:', window.location.href);
    console.log('location.search:', location.search);
    console.log('location.hash:', location.hash);
    console.log('提取的token:', tokenToUse);
    
    // 如果有 token，无论是否已登录都尝试使用 token 登录
    if (tokenToUse) {
      setIsTokenLoginLoading(true);
      console.log('使用token触发fetchUserProfileByToken:', tokenToUse);
      dispatch(fetchUserProfileByToken(tokenToUse))
        .then(result => {
          if (result.meta.requestStatus === 'fulfilled') {
            console.log('登录成功，重定向到干净的URL');
            // 清理 URL 并强制导航到当前页面
            // const cleanPath = location.pathname || '/';
            // const targetPath = '/index/aimonkey';
            // // window.history.replaceState(null, '', targetPath);
            // // window.history.replaceState(null, '', cleanPath);
            // navigate(targetPath, { replace: true }); // 强制导航以触发重新渲染

            // 使用完整的目标URL格式
            const targetUrl = '/list/#index/aimonkey';
            
            // 只使用history.replaceState方法
            window.history.replaceState(null, '', targetUrl);
          }
          setIsTokenLoginLoading(false);
        })
        .catch(error => {
          console.error('登录失败:', error);
          setIsTokenLoginLoading(false);
        });
    } else {
      const uid = localStorage.getItem('userId');
      console.log('没有url_login_token，检查uid:', uid);
      if (uid && !isLoggedIn) {
        dispatch(fetchUserProfile());
      }
    }
  }, [dispatch, location.search, location.hash, isLoggedIn]);

  const handleLoginClick = (e) => {
    e.preventDefault();
    setIsLoginModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsLoginModalOpen(false);
  };

  const handleLoginSuccess = () => {
    setIsLoginModalOpen(false);
  };

  // 主页组件
  const HomePage = () => {
    console.log('HomePage rendered');
    return (
      <>
        <SiteTransferNotice />
        <HomeProductSection id="fleet" />
        <FeatureSection id="features" />
        <HomeToolsSection id="fleet" />
      </>
    );
  };

  // 售后服务页面
// 售后服务页面 - 多语言版本
const AfterSalesPage = () => {
  console.log('AfterSalesPage rendered');
  return (
    <>
      <PageHeader
        titleKey="pages.afterSales.title"
        descriptionKey="pages.afterSales.description"
        breadcrumbsKey="pages.afterSales.breadcrumbs"
      />
      <AfterSales />
      {/* <HelpSection /> */}
    </>
  );
};


// 购买记录页面 - 多语言版本
const PurchasesPage = () => {
  console.log('PurchasesPage rendered');
  return (
    <>
      <PageHeader
        titleKey="pages.purchases.title"
        descriptionKey="pages.purchases.description"
        breadcrumbsKey="pages.purchases.breadcrumbs"
      />
      <Purchases />
    </>
  );
};

  // 产品详情页面
  const ProductDetailPage = () => {
    const { id } = useParams();
    const allProducts = [...mirrorProducts, ...finishedProducts];
    const product = allProducts.find((p) => p.id === id) || mirrorProducts[0];
    console.log('ProductDetailPage rendered:', { id });
    return (
      <>
        <PageHeader
          title={product.name}
          description="查看产品详情和购买选项"
          breadcrumbs={[
            { label: '首页', path: '/' },
            { label: '产品', path: '/products' },
            { label: product.name.includes('镜像') ? '镜像账户' : '成品号' },
            { label: product.name },
          ]}
        />
        <ProductDetail />
        <ProductDetailsVertical />
        <RelatedProducts />
      </>
    );
  };

  // 订单页面
  const OrderPage = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const productId = queryParams.get('productId');
    const product = location.state?.product || mirrorProducts[1];
    console.log('OrderPage rendered:', { productId });

    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [isPaymentInProgress, setIsPaymentInProgress] = useState(false);

    const handlePaymentComplete = () => {
      setIsPaymentInProgress(false);
    };

    return (
      <section className="order-container" style={{ padding: '50px 0', background: 'var(--light-bg)' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <div className="order-header" style={{ textAlign: 'center', marginBottom: '40px' }}>
            <h1 style={{ fontSize: '2.5rem', fontWeight: 700, color: 'var(--primary-color)', marginBottom: '10px' }}>
              完成您的订单
            </h1>
          </div>
          <div className="order-content" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '30px' }}>
            <OrderForm
              product={product}
              isPaymentModalOpen={isPaymentModalOpen}
              setIsPaymentModalOpen={setIsPaymentModalOpen}
              isPaymentInProgress={isPaymentInProgress}
              setIsPaymentInProgress={setIsPaymentInProgress}
              onPaymentComplete={handlePaymentComplete}
            />
            <OrderSummary
              product={product}
              isPaymentModalOpen={isPaymentModalOpen}
              isPaymentInProgress={isPaymentInProgress}
            />
          </div>
        </div>
      </section>
    );
  };

  // 订单确认页面
  const OrderConfirmationPage = () => {
    const { orderId } = useParams();
    console.log('OrderConfirmationPage rendered:', { orderId });
    return (
      <>
        <PageHeader
          title="订单确认"
          description="您的订单已成功提交，您可以在此查看订单详情和账户信息"
          breadcrumbs={[
            { label: '首页', path: '/' },
            { label: '购物车', path: '/cart' },
            { label: '结账', path: '/checkout' },
            { label: '订单确认' },
          ]}
        />
        <OrderConfirmation orderId={orderId} />
      </>
    );
  };

  //服务器车队页面

// 服务器车队页面 
const FleetPage = () => {
  return (
    <>
      <ServerFleet />
    </>
  );
};

// 导入翻译钩子





  // 新增的车队列表页面（带固定弹窗）
  const ChangeIPPage = () => {
    const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);

    // 在组件挂载时自动打开弹窗
    useEffect(() => {
      setIsAlertModalOpen(true); // 进入页面时自动显示弹窗
    }, []);

    console.log('CarListPage rendered');
    return (
      <>
        <PageHeader
          title="AI车队"
          description="每个节点都是独立的真实Plus账号，切换到新节点后需要开始新的对话。"
          breadcrumbs={[
            { label: '首页', path: '/' },
            { label: '车队列表', path: '/index/carList' }, // 面包屑稍作调整
          ]}
        />
        <ServerFleet />
        {/* 添加固定的 AlertModal */}
        <AlertModal
          isOpen={isAlertModalOpen}
          message="检测到您在其他设备上有新的登录操作，请关掉当前紫刀相关页面，右上角退出后重新登录，重新进入AI服务页面即可"
          onClose={() => setIsAlertModalOpen(false)}
          confirmText="知道了"
        />
      </>
    );
  };






  // 新增的车队列表页面（带固定弹窗）
  const RechargeOutPage = () => {
    const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);

    // 在组件挂载时自动打开弹窗
    useEffect(() => {
      setIsAlertModalOpen(true); // 进入页面时自动显示弹窗
    }, []);

    console.log('CarListPage rendered');
    return (
      <>
        <PageHeader
          title="AI车队"
          description="每个节点都是独立的真实Plus账号，切换到新节点后需要开始新的对话。"
          breadcrumbs={[
            { label: '首页', path: '/' },
            { label: '车队列表', path: '/index/carList' }, // 面包屑稍作调整
          ]}
        />
        <ServerFleet />
        {/* 添加固定的 AlertModal */}
        <AlertModal
          isOpen={isAlertModalOpen}
          message="系统检测您的套餐已到期，请及时续费"
          onClose={() => setIsAlertModalOpen(false)}
          confirmText="知道了"
        />
      </>
    );
  };








  // 新增的车队列表页面（带固定弹窗）
  const AIMonkeyPage = () => {
    const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);

    // 在组件挂载时自动打开弹窗
    useEffect(() => {
      setIsAlertModalOpen(true); // 进入页面时自动显示弹窗
    }, []);

    console.log('CarListPage rendered');
    return (
      <>
        <Hero />
        <HomeProductSection id="fleet" title="AI服务" />
        <FeatureSection id="features" title="我们的优势" features={features} />
        <HomeToolsSection id="fleet" title="工具箱" />
    
        {/* 添加固定的 AlertModal */}
        <AlertModal
          isOpen={isAlertModalOpen}
          message="您已购买成功，请选择您对应的AI服务，点击【立即使用】即可。或者在【AI车队】页面自选节点进入"
          onClose={() => setIsAlertModalOpen(false)}
          confirmText="知道了"
        />
      </>
    );
  };






  

  return (
    <div>
      <Header onLoginClick={handleLoginClick} />
      
      {/* 添加token登录加载指示器 */}
      {isTokenLoginLoading && (
        <div className="token-login-loading" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div className="spinner" style={{
            width: '50px',
            height: '50px',
            border: '5px solid #f3f3f3',
            borderTop: '5px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '20px'
          }}></div>
          <div style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#333'
          }}>
            正在登录中，请稍候...
          </div>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      )}
      
      <Routes>
        <Route path="/" element={<HomePage />} />          {/* 多语言适配 */}
        <Route path="/account" element={<AfterSalesPage />} />        {/* 多语言适配 */}
        <Route path="/cart" element={<PurchasesPage />} />
        <Route path="/product/:id" element={<ProductDetailPage />} />
        <Route path="/order" element={<OrderPage />} />
        <Route path="/order-confirmation/:orderId" element={<OrderConfirmationPage />} />
        <Route path="/:category" element={<ProductSeriesPage />} />
        <Route path="/fleet" element={<FleetPage />} />
        <Route path="/invite" element={<InvitePage />} />
        <Route path="/index/carList" element={<FleetPage />} />
        <Route path="/index/change_ip" element={<ChangeIPPage />} />        
        <Route path="/index/recharge_out" element={<RechargeOutPage />} />        
        <Route path="/index/home" element={<HomePage />} />
        <Route path="/index/aimonkey" element={<AIMonkeyPage />} />
        <Route path="/auto-login" element={<AutoLogin />} />


      </Routes>
      <Footer />
      <LoginModal isOpen={isLoginModalOpen} onClose={handleCloseModal} onLoginSuccess={handleLoginSuccess} />
      {/* <FloatingMenu /> */}
    </div>
  );
};

export default App;

